<template>
  <section
    class="font-sans text-slate-900 bg-gradient-to-br from-slate-50 to-blue-50/30"
  >
    <!-- Loading overlay -->
    <div
      v-if="loading"
      class="fixed inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center"
    >
      <div
        class="flex items-center gap-3 bg-white rounded-2xl p-6 shadow-xl border border-slate-200"
      >
        <Loader2 class="animate-spin h-6 w-6 text-blue-600" />
        <span class="text-slate-700 font-medium">{{ loadingMessage }}</span>
      </div>
    </div>

    <div class="space-y-8">
      <!-- Toast Notifications -->
      <div class="fixed top-4 right-4 z-[10000] space-y-3">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="max-w-sm rounded-xl border p-4 shadow-xl backdrop-blur-sm transition-all duration-300 transform animate-in slide-in-from-right-full"
          :class="getToastClass(toast.type)"
        >
          <div class="flex items-center gap-3">
            <div
              class="size-9 rounded-full grid place-items-center border-2 bg-white shadow-sm"
              :class="getToastIconClass(toast.type)"
            >
              <CheckCircle2
                v-if="toast.type === 'success'"
                class="size-5"
              />
              <AlertCircle
                v-else
                class="size-5"
              />
            </div>
            <div class="flex-1">
              <p
                class="font-semibold text-sm"
                :class="getToastTitleClass(toast.type)"
              >
                {{ getToastTitle(toast.type) }}
              </p>
              <p
                class="text-sm"
                :class="getToastMessageClass(toast.type)"
              >
                {{ toast.message }}
              </p>
            </div>
            <button
              @click="removeToast(toast.id)"
              class="transition-colors p-1 rounded-lg hover:bg-white/50"
              :class="getToastButtonClass(toast.type)"
            >
              <X class="size-4" />
            </button>
          </div>
        </div>
      </div>

      <!-- Header row -->
      <div class="flex items-center justify-between">
        <div>
          <h1
            class="text-[10px] sm:text-xl text-slate-800 mb-2 flex items-center gap-2"
          >
            <CheckCircle class="w-8 h-8" />Manage and Apply for Leaves
          </h1>
        </div>

        <button
          class="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 text-sm font-semibold text-white hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
          @click="openModal"
          :disabled="loading"
        >
          <Plus class="size-5" />
          New Request
        </button>
      </div>

      <!-- Filters -->
      <div class="rounded-2xl p-6 border bg-gray-600 border-gray-800 shadow-lg">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <Filter class="size-5 text-white" />
            <h3 class="font-semibold text-white text-lg">Filters</h3>
          </div>

          <!-- Hamburger menu button for mobile -->
          <button
            @click="showMobileFilters = !showMobileFilters"
            class="md:hidden p-2 rounded-lg hover:bg-gray-700 transition-colors"
            :class="{ 'bg-gray-700': showMobileFilters }"
          >
            <svg
              class="size-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end transition-all duration-300"
          :class="{
            'hidden md:grid': !showMobileFilters,
            grid: showMobileFilters,
          }"
        >
          <div class="space-y-2">
            <label class="text-sm font-medium text-white">Search</label>
            <div class="relative">
              <Search
                class="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                v-model="filters.search"
                type="text"
                class="w-full rounded-xl bg-gray-700 text-white placeholder-gray-400 px-10 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-400 outline-none transition"
                placeholder="
Search by  reason
                "
                @keyup.enter="fetchLeaves"
              />
            </div>
          </div>

          <div class="space-y-2">
            <label class="text-sm font-medium text-white">Leave Type</label>
            <div class="relative">
              <select
                v-model="filters.leave_type"
                class="w-full rounded-xl bg-gray-700 text-white text-sm px-3 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 outline-none transition"
              >
                <option value="">All Types</option>
                <option
                  v-for="type in leaveTypes"
                  :key="type"
                  :value="type"
                >
                  {{ type }}
                </option>
              </select>
              <ChevronDown
                class="size-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>

          <div class="space-y-2">
            <label class="text-sm font-medium text-white">Month</label>
            <div class="relative">
              <select
                v-model="filters.month"
                class="w-full rounded-xl bg-gray-700 text-white text-sm px-3 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 outline-none transition"
              >
                <option value="">All Months</option>
                <option
                  v-for="(month, index) in months"
                  :key="index"
                  :value="index + 1"
                >
                  {{ month }}
                </option>
              </select>
              <ChevronDown
                class="size-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>

          <div class="space-y-2">
            <label class="text-sm font-medium text-white">Year</label>
            <div class="relative">
              <select
                v-model="filters.year"
                class="w-full rounded-xl bg-gray-700 text-white text-sm px-3 py-3 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 outline-none transition"
              >
                <option
                  v-for="year in availableYears"
                  :key="year"
                  :value="year"
                >
                  {{ year }}
                </option>
              </select>
              <ChevronDown
                class="size-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>

          <div class="flex flex-col sm:flex-row gap-2">
            <button
              class="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-500 text-white px-4 py-3 text-sm font-medium hover:bg-gray-400 disabled:opacity-50 transition"
              @click="clearFilters"
              :disabled="loading"
            >
              <RotateCcw class="size-4 text-white" />
              Clear
            </button>
            <button
              class="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 text-white px-4 py-3 text-sm font-medium hover:bg-blue-500 disabled:opacity-50 transition shadow"
              @click="fetchLeaves"
              :disabled="loading"
            >
              <Filter class="size-4 text-white" />
              {{ getFilterButtonText }}
            </button>
          </div>
        </div>
      </div>
      <!-- Leave Balance Cards -->
      <!-- Leave Balance Cards (Only Non-Admin) -->
      <div
        v-if="userRole !== 'admin' && leaveBalance && leaveBalance.balances"
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <div
          v-for="item in leaveBalance.balances"
          :key="item.type"
          class="rounded-2xl border bg-white p-4 shadow-sm"
        >
          <div class="flex justify-between items-center mb-2">
            <span class="font-semibold text-slate-700">
              {{ item.type }}
            </span>
            <span
              class="text-sm font-bold"
              :class="item.remaining <= 2 ? 'text-red-600' : 'text-green-600'"
            >
              {{ item.remaining }} left
            </span>
          </div>

          <!-- Progress bar -->
          <div class="w-full bg-slate-100 rounded-full h-2">
            <div
              class="h-2 rounded-full bg-blue-600"
              :style="{
                width: (item.used / item.total) * 100 + '%',
              }"
            ></div>
          </div>

          <div class="text-xs text-slate-500 mt-1">
            Used: {{ item.used }} / {{ item.total }}
          </div>
        </div>
      </div>
      <!-- Stats -->
      <div class="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <div
          class="group relative overflow-hidden rounded-2xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-6 shadow-sm hover:shadow-md transition-all duration-200"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="flex items-center gap-3 mb-2">
                <div
                  class="size-10 rounded-xl bg-emerald-600 text-white grid place-items-center shadow-lg"
                >
                  <CheckCircle class="size-5" />
                </div>
                <span class="text-3xl font-bold text-emerald-700">{{
                  getTotalApproved
                }}</span>
              </div>
              <div class="text-sm font-medium text-slate-600">Approved</div>
            </div>
          </div>
          <div
            class="absolute -right-4 -top-4 size-16 rounded-full bg-emerald-200/30 group-hover:scale-110 transition-transform duration-300"
          ></div>
        </div>

        <div
          class="group relative overflow-hidden rounded-2xl border border-amber-200/60 bg-gradient-to-br from-amber-50 to-amber-100/50 p-6 shadow-sm hover:shadow-md transition-all duration-200"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="flex items-center gap-3 mb-2">
                <div
                  class="size-10 rounded-xl bg-amber-600 text-white grid place-items-center shadow-lg"
                >
                  <Clock class="size-5" />
                </div>
                <span class="text-3xl font-bold text-amber-700">{{
                  getTotalPending
                }}</span>
              </div>
              <div class="text-sm font-medium text-slate-600">Pending</div>
            </div>
          </div>
          <div
            class="absolute -right-4 -top-4 size-16 rounded-full bg-amber-200/30 group-hover:scale-110 transition-transform duration-300"
          ></div>
        </div>

        <div
          class="group relative overflow-hidden rounded-2xl border border-red-200/60 bg-gradient-to-br from-red-50 to-red-100/50 p-6 shadow-sm hover:shadow-md transition-all duration-200"
        >
          <div class="flex items-center justify-between">
            <div>
              <div class="flex items-center gap-3 mb-2">
                <div
                  class="size-10 rounded-xl bg-red-600 text-white grid place-items-center shadow-lg"
                >
                  <XCircle class="size-5" />
                </div>
                <span class="text-3xl font-bold text-red-700">{{
                  getTotalDeclined
                }}</span>
              </div>
              <div class="text-sm font-medium text-slate-600">Rejected</div>
            </div>
          </div>
          <div
            class="absolute -right-4 -top-4 size-16 rounded-full bg-red-200/30 group-hover:scale-110 transition-transform duration-300"
          ></div>
        </div>
      </div>

      <!-- Mobile cards -->
      <ul class="grid gap-4 lg:hidden">
        <li
          v-for="leave in leaves"
          :key="leave._id"
          class="group relative overflow-hidden rounded-2xl border bg-gradient-to-br from-white to-slate-50/30 p-4 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-slate-300/60 transition-all duration-300 hover:-translate-y-1"
        >
          <div
            class="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          ></div>

          <div class="flex items-center justify-between relative mb-4">
            <div
              class="inline-flex items-center h-7 rounded-full px-3 text-xs font-bold tracking-wide shadow-sm ring-1 ring-white/20"
              :class="badgeClass(leave.status)"
            >
              {{ leave.status }}
            </div>
            <div class="ml-3">
              <span
                class="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2.5 py-1 rounded-lg font-bold text-xs shadow-md shadow-blue-200/50 ring-1 ring-blue-400/30"
              >
                {{ leave.user_id?.name || "—" }}
              </span>
            </div>

            <button
              class="size-8 rounded-xl border border-slate-200/60 bg-white/80 backdrop-blur-sm grid place-items-center hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 transition-all duration-200 shadow-sm hover:shadow-md"
              @click="toggleRowMenu(leave._id)"
              :disabled="loading"
            >
              <MoreVertical class="size-3.5 text-slate-600" />
            </button>
            <div
              v-if="rowMenuId === leave._id"
              class="absolute right-0 top-12 z-[999] min-w-40 rounded-xl border border-slate-200/60 bg-white/95 backdrop-blur-md p-2.5 shadow-2xl shadow-slate-900/10 ring-1 ring-slate-200/50"
            >
              <button
                class="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-red-50 disabled:opacity-50 text-red-700 hover:text-red-700 transition-all duration-200"
                @click="cancelLeaveRequest(leave._id)"
                :disabled="leave.status !== 'Pending' || loading"
              >
                <Trash2 class="size-3.5" /> Cancel Request
              </button>
            </div>
          </div>

          <!-- <div
            class="flex items-center gap-2 text-sm text-slate-600 mb-3 p-2.5 bg-slate-50/50 rounded-lg border border-slate-100"
          >
            <div
              class="flex items-center justify-center w-5 h-5 bg-slate-200 rounded-md"
            >
              <span class="text-xs">📧</span>
            </div>
            <span class="font-semibold text-slate-700">{{
              leave.user_id?.email || "—"
            }}</span>
          </div> -->

          <div class="flex items-center gap-2.5 mb-4">
            <div
              class="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-200/50"
            >
              <FileText class="size-4 text-white" />
            </div>
            <div class="text-lg font-bold text-slate-900 tracking-tight">
              {{ leave.leave_type }}
            </div>
          </div>

          <div class="space-y-3 text-sm">
            <div
              class="flex items-center gap-3 p-2.5 bg-slate-50/50 rounded-lg"
            >
              <div
                class="flex items-center gap-2 text-slate-500 min-w-[80px] font-medium"
              >
                <div
                  class="flex items-center justify-center w-5 h-5 bg-green-100 rounded-md"
                >
                  <Calendar class="size-3 text-green-600" />
                </div>
                <span>From :</span>
              </div>
              <span class="text-slate-700">
                {{ formatDateTime(leave.from_datetime) }}
                <strong class="font-bold text-green-700 ml-1 capitalize"
                  >({{ leave.start_time }})</strong
                >
              </span>
            </div>

            <div
              class="flex items-center gap-3 p-2.5 bg-slate-50/50 rounded-lg"
            >
              <div
                class="flex items-center gap-2 text-slate-500 min-w-[80px] font-medium"
              >
                <div
                  class="flex items-center justify-center w-5 h-5 bg-red-100 rounded-md"
                >
                  <Calendar class="size-3 text-red-600" />
                </div>
                <span>To :</span>
              </div>
              <span class="text-slate-700">
                {{ formatDateTime(leave.to_datetime) }}
                <strong class="font-bold text-red-700 ml-1 capitalize"
                  >({{ leave.end_time }})</strong
                >
              </span>
            </div>

            <div
              class="flex items-center gap-3 p-2.5 bg-slate-50/50 rounded-lg"
            >
              <div
                class="flex items-center gap-2 text-slate-500 min-w-[80px] font-medium"
              >
                <div
                  class="flex items-center justify-center w-5 h-5 bg-purple-100 rounded-md"
                >
                  <Clock class="size-3 text-purple-600" />
                </div>
                <span>Days :</span>
              </div>
              <strong class="font-bold text-slate-900">{{
                leave.duration_days
              }}</strong>
            </div>

            <div class="flex items-start gap-3 p-2.5 bg-slate-50/50 rounded-lg">
              <div
                class="flex items-center gap-2 text-slate-500 min-w-[80px] font-medium"
              >
                <div
                  class="flex items-center justify-center w-5 h-5 bg-amber-100 rounded-md"
                >
                  <MessageSquare class="size-3 text-amber-600" />
                </div>
                <span>Reason :</span>
              </div>
              <span
                class="text-slate-700 font-medium leading-relaxed truncate"
                :title="leave.reason"
                >{{ leave.reason }}</span
              >
            </div>
          </div>
        </li>

        <li
          v-if="leaves.length === 0 && !loading"
          class="rounded-2xl border-2 border-dashed border-slate-300/60 p-8 text-center text-slate-500 bg-gradient-to-br from-slate-50/50 to-white"
        >
          <div class="flex flex-col items-center gap-3">
            <div
              class="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-xl"
            >
              <FileX class="size-6 text-slate-400" />
            </div>
            <div class="space-y-1">
              <p class="font-bold text-slate-700 text-base">No leaves found</p>
              <p class="text-sm text-slate-500 max-w-sm">
                Try adjusting your filters or create a new request
              </p>
            </div>
          </div>
        </li>
      </ul>

      <!-- Desktop table -->
      <div
        class="hidden lg:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
      >
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-slate-50">
              <tr class="text-left text-slate-700">
                <th class="px-6 py-4 font-semibold">
                  <div class="flex items-center gap-2">
                    <Hash class="w-4 h-4" />
                  </div>
                </th>
                <!-- Name column (admin only) -->
                <!-- <th
                  v-if="userRole === 'admin'"
                  class="px-6 py-4 font-semibold"
                >
                  <div class="flex items-center gap-2">
                    <User class="w-4 h-4" />
                    Name <Mail class="w-4 h-4" />
                    Email
                  </div>
                </th> -->

                <th class="px-6 py-4 font-semibold">
                  <div class="flex items-center gap-2">
                    <List class="w-4 h-4" />
                    Type
                  </div>
                </th>
                <th class="px-6 py-4 font-semibold">
                  <div class="flex items-center gap-2">
                    <Calendar class="w-4 h-4" />
                    From
                  </div>
                </th>
                <th class="px-6 py-4 font-semibold">
                  <div class="flex items-center gap-2">
                    <Calendar class="w-4 h-4" />
                    To
                  </div>
                </th>
                <th class="px-6 py-4 font-semibold">
                  <div class="flex items-center gap-2">
                    <Clock class="w-4 h-4" />
                    Days
                  </div>
                </th>
                <th class="px-6 py-4 font-semibold">
                  <div class="flex items-center gap-2">
                    <MessageCircle class="w-4 h-4" />
                    Reason
                  </div>
                </th>
                <th class="px-6 py-4 font-semibold">
                  <div class="flex items-center gap-2">
                    <CheckCircle class="w-4 h-4" />
                    Status
                  </div>
                </th>
                <th class="px-6 py-4 font-semibold w-1">
                  <div class="flex items-center gap-2">
                    <Settings class="w-4 h-4" />
                    Action
                  </div>
                </th>
              </tr>
            </thead>

            <tbody class="divide-y divide-slate-100">
              <tr
                v-for="(leave, i) in leaves"
                :key="leave._id"
                class="hover:bg-slate-50/50 transition-colors"
              >
                <td class="px-6 py-4 font-medium text-slate-600">
                  {{ getRowNumber(i) }}
                </td>
                <!-- <td
                  v-if="userRole === 'admin'"
                  class="px-6 py-4 text-slate-700 font-medium"
                >
                  <div class="flex flex-col gap-1">
                    <div class="flex items-center gap-2">
                      <User class="w-4 h-4 text-blue-500" />
                      <span>{{ leave.user_id?.name || "—" }}</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <Mail class="w-4 h-4 text-green-500" />
                      <span
                        class="truncate"
                        style="font-size: 11px"
                      >
                        {{ leave.user_id?.email || "—" }}
                      </span>
                    </div>
                  </div>
                </td> -->

                <td class="px-6 py-4">
                  <div class="flex items-center gap-2">
                    <FileText class="size-4 text-blue-600" />
                    <span class="font-medium">{{ leave.leave_type }}</span>
                  </div>
                </td>
                <td class="px-6 py-4 text-slate-700">
                  {{ formatDateTime(leave.from_datetime) }}
                  <span class="font-bold text-blue-400 ml-1 capitalize"
                    >({{ leave.start_time }})</span
                  >
                </td>

                <td class="px-6 py-4 text-slate-700">
                  {{ formatDateTime(leave.to_datetime) }}
                  <span class="font-bold text-blue-400 ml-1 capitalize"
                    >({{ leave.end_time }})</span
                  >
                </td>

                <td class="px-5 py-4">
                  <span
                    class="inline-flex items-center gap-1 px-1 py-1.5 text-orange-500 font-bold rounded-xl shadow-sm shadow-purple-200/50"
                  >
                    <Clock class="size-4" />
                    {{ leave.duration_days }}
                  </span>
                </td>

                <td
                  class="px-6 py-4 max-w-[300px] truncate text-slate-700"
                  :title="leave.reason"
                >
                  {{ leave.reason }}
                </td>
                <td class="px-6 py-4">
                  <span
                    class="inline-flex items-center h-7 rounded-full px-3 text-xs font-semibold"
                    :class="badgeClass(leave.status)"
                  >
                    {{ leave.status }}
                  </span>
                </td>
                <td class="px-6 py-4 relative whitespace-nowrap">
                  <button
                    class="size-9 rounded-xl border border-slate-200 grid place-items-center hover:bg-slate-50 disabled:opacity-50 transition-colors"
                    @click="toggleRowMenu(leave._id)"
                    :disabled="loading"
                  >
                    <MoreVertical class="size-4" />
                  </button>
                  <div
                    v-if="rowMenuId === leave._id"
                    class="absolute right-[88px] top-[7px] z-50 min-w-40 rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
                  >
                    <button
                      class="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-red-50 disabled:opacity-50 text-red-600"
                      @click="cancelLeaveRequest(leave._id)"
                      :disabled="leave.status !== 'Pending' || loading"
                    >
                      <Trash2 class="size-4" /> Cancel Request
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="leaves.length === 0 && !loading">
                <td
                  colspan="8"
                  class="px-6 py-12 text-center"
                >
                  <div class="flex flex-col items-center gap-3 text-slate-500">
                    <FileX class="size-12 text-slate-400" />
                    <div>
                      <p class="font-medium">No leaves found</p>
                      <p class="text-sm">
                        Try adjusting your filters or create a new request
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <nav
        v-if="pagination.totalPages > 1"
        class="flex items-center justify-center gap-4 p-4"
      >
        <button
          class="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-all"
          :disabled="pagination.page === 1 || loading"
          @click="changePage(pagination.page - 1)"
        >
          <ChevronLeft class="size-4" /> Previous
        </button>
        <div class="flex items-center gap-2 text-sm text-slate-600">
          <span>Page</span>
          <span class="font-semibold text-slate-900">{{
            pagination.page
          }}</span>
          <span>of</span>
          <span class="font-semibold text-slate-900">{{
            pagination.totalPages
          }}</span>
        </div>
        <button
          class="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-all"
          :disabled="pagination.page === pagination.totalPages || loading"
          @click="changePage(pagination.page + 1)"
        >
          Next <ChevronRight class="size-4" />
        </button>
      </nav>
    </div>

    <!-- Modal -->

    <!-- Modal for new leave request -->
    <div
      v-if="modalOpen"
      class="fixed inset-0 z-[1005] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      @click.self="closeModal"
    >
      <div
        class="w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div
          class="flex items-center justify-between border-b border-slate-200 p-6"
        >
          <div class="flex items-center gap-4">
            <div
              class="size-10 rounded-xl bg-blue-600 text-white grid place-items-center"
            >
              <Plus class="size-5" />
            </div>
            <div>
              <h3 class="font-semibold text-slate-900">New Leave Request</h3>
              <p class="text-sm text-slate-600">
                Submit your leave application
              </p>
            </div>
          </div>
          <button
            class="size-9 rounded-xl border border-slate-200 grid place-items-center hover:bg-white transition-colors"
            @click="closeModal"
          >
            <X class="size-4" />
          </button>
        </div>

        <form
          @submit.prevent="submitLeaveRequest"
          novalidate
        >
          <div class="grid gap-6 p-6">
            <!-- Leave Type -->
            <div class="space-y-2">
              <label
                for="type"
                class="block text-sm font-semibold text-slate-700"
              >
                Leave Type<span class="text-red-500 ml-1">*</span>
              </label>

              <div class="relative">
                <select
                  id="type"
                  v-model="form.leaveType"
                  :class="[
                    'w-full rounded-xl border bg-white px-4 py-3 text-sm outline-none transition-all appearance-none',
                    errors.leaveType
                      ? 'border-red-400 focus:ring-2 focus:ring-red-500/20'
                      : 'border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20',
                  ]"
                  required
                >
                  <option value="">Select leave type</option>

                  <option
                    v-for="item in leaveBalance?.balances || []"
                    :key="item.type"
                    :value="item.type"
                    :disabled="userRole !== 'admin' && item.remaining <= 0"
                  >
                    {{ item.type }}
                    <span v-if="userRole !== 'admin'">
                      ({{ item.remaining }} left)
                    </span>
                  </option>
                </select>

                <ChevronDown
                  class="size-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
              </div>

              <!-- Remaining Balance Info -->
              <div
                v-if="userRole !== 'admin' && form.leaveType"
                class="text-xs mt-1"
              >
                <span class="text-slate-500">Remaining:</span>
                <span
                  :class="[
                    'font-semibold ml-1',
                    selectedLeaveRemaining <= 2
                      ? 'text-red-600'
                      : 'text-green-600',
                  ]"
                >
                  {{ selectedLeaveRemaining }} day(s)
                </span>
              </div>

              <!-- Low Balance Warning -->
              <div
                v-if="
                  userRole !== 'admin' &&
                  form.leaveType &&
                  selectedLeaveRemaining <= 2
                "
                class="text-xs text-amber-600 flex items-center gap-1 mt-1"
              >
                <AlertCircle class="size-3" />
                Low leave balance remaining.
              </div>

              <!-- Error Message -->
              <p
                v-if="errors.leaveType"
                class="text-xs text-red-600 flex items-center gap-1 mt-1"
              >
                <AlertCircle class="size-3" />
                {{ errors.leaveType }}
              </p>
            </div>

            <!-- From Date and Duration -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="space-y-2">
                <label
                  for="fromDate"
                  class="block text-sm font-semibold text-slate-700"
                >
                  From Date<span class="text-red-500 ml-1">*</span>
                </label>
                <input
                  id="fromDate"
                  v-model="form.fromDate"
                  type="date"
                  class="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 transition-all"
                  required
                />
                <p
                  v-if="errors.fromDate"
                  class="text-xs text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="size-3" />
                  {{ errors.fromDate }}
                </p>
              </div>

              <div class="space-y-2">
                <label
                  for="fromDuration"
                  class="block text-sm font-semibold text-slate-700"
                >
                  From Duration<span class="text-red-500 ml-1">*</span>
                </label>
                <div class="relative">
                  <select
                    id="fromDuration"
                    v-model="form.fromDuration"
                    class="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 transition-all appearance-none"
                    required
                  >
                    <!-- <option
                      value=""
                      disabled
                    >
                      Select duration
                    </option> -->
                    <option
                      value="first half"
                      disabled
                    >
                      First Half
                    </option>
                    <option value="second half">Second Half</option>
                    <option value="full day">Full Day</option>
                  </select>
                  <ChevronDown
                    class="size-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                </div>
                <p
                  v-if="errors.fromDuration"
                  class="text-xs text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="size-3" />
                  {{ errors.fromDuration }}
                </p>
              </div>
            </div>

            <!-- To Date and Duration -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="space-y-2">
                <label
                  for="toDate"
                  class="block text-sm font-semibold text-slate-700"
                >
                  To Date<span class="text-red-500 ml-1">*</span>
                </label>
                <input
                  id="toDate"
                  v-model="form.toDate"
                  type="date"
                  :min="form.fromDate || ''"
                  class="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 transition-all"
                  required
                />
                <p
                  v-if="errors.toDate"
                  class="text-xs text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="size-3" />
                  {{ errors.toDate }}
                </p>
              </div>

              <div class="space-y-2">
                <label
                  for="toDuration"
                  class="block text-sm font-semibold text-slate-700"
                >
                  To Duration<span class="text-red-500 ml-1">*</span>
                </label>
                <div class="relative">
                  <select
                    id="toDuration"
                    v-model="form.toDuration"
                    class="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 transition-all appearance-none"
                    required
                  >
                    <!-- <option
                      value=""
                      disabled
                    >
                      Select duration
                    </option> -->
                    <option value="first half">First Half</option>
                    <option
                      value="second half"
                      disabled
                    >
                      Second Half
                    </option>
                    <option value="full day">Full Day</option>
                  </select>
                  <ChevronDown
                    class="size-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                </div>
                <p
                  v-if="errors.toDuration"
                  class="text-xs text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="size-3" />
                  {{ errors.toDuration }}
                </p>
              </div>
            </div>
            <div class="text-sm text-slate-600 mt-2">
              Total Leave Duration:
              <span class="font-semibold">{{ totalLeaveDays }}</span>
            </div>

            <!-- Reason -->
            <div class="space-y-2">
              <label
                for="reason"
                class="block text-sm font-semibold text-slate-700"
              >
                Reason<span class="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="reason"
                v-model="form.reason"
                rows="4"
                placeholder="Please provide a detailed reason for your leave request..."
                class="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none"
                required
              ></textarea>
              <div class="flex justify-between items-center">
                <p
                  v-if="errors.reason"
                  class="text-xs text-red-600 flex items-center gap-1"
                >
                  <AlertCircle class="size-3" />
                  {{ errors.reason }}
                </p>
                <p class="text-xs text-slate-500 ml-auto">
                  {{ form.reason.length }}/500 characters
                </p>
              </div>
            </div>
          </div>
          <!-- Submit Error -->
          <p
            v-if="submitError"
            class="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2 mx-6 mb-4 flex items-center gap-2"
          >
            <AlertCircle class="size-4" />
            {{ submitError }}
          </p>
          <div
            class="flex items-center justify-between gap-4 border-t border-slate-200 px-6 py-4 bg-slate-50"
          >
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all"
              @click="closeModal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 text-sm font-semibold text-white hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 transition-all duration-200 shadow-lg hover:shadow-xl"
              :disabled="loading"
            >
              <Send class="size-4" />
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, watch } from "vue";
  import { useStore } from "vuex";
  import {
    Calendar,
    Clock,
    Filter,
    Search,
    Plus,
    CheckCircle,
    CheckCircle2,
    XCircle,
    AlertCircle,
    FileText,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    MoreVertical,
    X,
    Trash2,
    FileX,
    MessageSquare,
    Send,
    RotateCcw,
    Loader2,
    Hash,
    List,
    MessageCircle,
    Settings,
    // User,
    // Mail,
  } from "lucide-vue-next";

  const store = useStore();
  const userRole = computed(() => store.getters.getUserRole);
  const leaveBalance = computed(() => store.getters.getLeaveBalance);
  // Reactive state
  const loading = ref(false);
  const loadingMessage = ref("");
  const modalOpen = ref(false);
  const showMobileFilters = ref(false);
  const rowMenuId = ref(null);
  const submitError = ref("");

  // Toast notification system
  const toasts = ref([]);

  // Leave types
  const leaveTypes = computed(
    () => leaveBalance.value?.balances?.map((b) => b.type) || [],
  );

  // Months for filtering
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Available years for year selector
  const availableYears = computed(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 1; i++) {
      years.push(i);
    }
    return years;
  });

  // Form data
  const form = reactive({
    leaveType: "",
    fromDate: "",
    fromDuration: "full day",
    toDate: "",
    toDuration: "full day",
    reason: "",
  });

  // Form errors
  const errors = reactive({
    leaveType: "",
    fromDate: "",
    fromDuration: "",
    toDate: "",
    toDuration: "",
    reason: "",
  });

  // Filters
  const filters = reactive({
    search: "",
    leave_type: "",
    month: "",
    year: new Date().getFullYear(),
  });

  // Data from store
  const leaves = computed(() => store.state.leaveRequests || []);

  const leaveSummary = computed(
    () =>
      store.state.leaveSummary || {
        totalLeaves: 0,
        totalApproved: 0,
        totalPending: 0,
        totalDeclined: 0,
      },
  );

  // Pagination
  const pagination = ref({
    page: 1,
    limit: 20,
    totalPages: 1,
    total: 0,
  });

  // const getTotalLeaves = computed(() => leaveSummary.value.totalLeaves || 0);
  const getTotalApproved = computed(
    () => leaveSummary.value.totalApproved || 0,
  );
  const getTotalPending = computed(() => leaveSummary.value.totalPending || 0);
  const getTotalDeclined = computed(
    () => leaveSummary.value.totalDeclined || 0,
  );

  const getFilterButtonText = computed(() =>
    loading.value ? "Filtering..." : "Apply",
  );

  function getToastClass(type) {
    return type === "success"
      ? "border-emerald-200 bg-emerald-50/90"
      : "border-red-200 bg-red-50/90";
  }

  function getToastIconClass(type) {
    return type === "success"
      ? "border-emerald-200 text-emerald-600"
      : "border-red-200 text-red-600";
  }

  function getToastTitleClass(type) {
    return type === "success" ? "text-emerald-800" : "text-red-800";
  }

  function getToastMessageClass(type) {
    return type === "success" ? "text-emerald-700" : "text-red-700";
  }

  function getToastButtonClass(type) {
    return type === "success"
      ? "text-emerald-600 hover:text-emerald-800"
      : "text-red-600 hover:text-red-800";
  }

  function getToastTitle(type) {
    return type === "success" ? "Success" : "Error";
  }

  function getRowNumber(index) {
    return (pagination.value.page - 1) * pagination.value.limit + index + 1;
  }

  // Toast notification methods
  function showToast(message, type = "success") {
    const id = Date.now();
    toasts.value.push({ id, message, type });

    // Auto remove after 5 seconds
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  }

  function removeToast(id) {
    const index = toasts.value.findIndex((toast) => toast.id === id);
    if (index > -1) {
      toasts.value.splice(index, 1);
    }
  }

  // Methods
  async function fetchLeaves() {
    try {
      loading.value = true;
      loadingMessage.value = "Loading leaves...";

      const params = {
        page: pagination.value.page,
        limit: pagination.value.limit,
        search: filters.search,
        leave_type: filters.leave_type,
        month: filters.month,
        year: filters.year,
        view: "true",
      };

      const result = await store.dispatch("fetchMyLeaves", params);

      if (result && result.pagination) {
        pagination.value = {
          page: result.pagination.page || 1,
          limit: result.pagination.limit || 10,
          totalPages: result.pagination.totalPages || 1,
          total: result.pagination.total || 0,
        };
      }
    } catch (err) {
      showToast(
        err.response?.data?.message || err.message || "Failed to fetch leaves",
        "error",
      );
    } finally {
      loading.value = false;
      loadingMessage.value = "";
    }
  }

  async function submitLeaveRequest() {
    if (!validateForm()) {
      showToast("Please fix the errors in the form.", "error");
      return;
    }

    try {
      loading.value = true;
      loadingMessage.value = "Submitting leave request...";
      // Calculate total leave duration as a number (e.g., 1.5)
      const fromDate = new Date(form.fromDate);
      const toDate = new Date(form.toDate);
      let days = (toDate - fromDate) / (1000 * 60 * 60 * 24) + 1;
      if (days < 0) days = 0;

      let totalDuration = days;
      if (
        form.fromDuration === "first half" ||
        form.fromDuration === "second half"
      ) {
        totalDuration -= 0.5;
      }
      if (
        form.toDuration === "first half" ||
        form.toDuration === "second half"
      ) {
        totalDuration -= 0.5;
      }

      // const integerPart = Math.floor(totalDuration);
      // const fractionalPart = totalDuration - integerPart;
      // let formattedDuration = "";

      // if (totalDuration <= 0) {
      //   formattedDuration = "Half day";
      // } else if (fractionalPart >= 0.5) {
      //   formattedDuration = `${integerPart}/half day`;
      // } else {
      //   formattedDuration = `${integerPart} day${integerPart > 1 ? "s" : ""}`;
      // }

      const leaveData = {
        leaveType: form.leaveType,
        fromDate: form.fromDate,
        fromDuration: form.fromDuration,
        toDate: form.toDate,
        toDuration: form.toDuration,
        reason: form.reason.trim(),

        duration_days: totalDuration,
      };
      const selectedType = leaveBalance.value?.balances?.find(
        (b) => b.type === form.leaveType,
      );

      if (!selectedType) {
        showToast("Leave type not configured.", "error");
        return;
      }

      if (selectedType.remaining <= 0) {
        showToast(`No ${form.leaveType} leaves remaining.`, "error");
        return;
      }
      await store.dispatch("applyLeave", leaveData);

      showToast("Leave request submitted successfully!");

      closeModal();
      resetForm();
      await fetchLeaves(); // Refresh the list
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to submit leave request";

      submitError.value = message; // ✅ now defined
      showToast(message, "error"); // optional but good
    } finally {
      loading.value = false;
      loadingMessage.value = "";
    }
  }

  async function cancelLeaveRequest(leaveId) {
    if (!confirm("Are you sure you want to cancel this leave request?")) {
      return;
    }

    try {
      loading.value = true;
      loadingMessage.value = "Cancelling leave request...";

      await store.dispatch("cancelLeave", leaveId);

      showToast("Leave request cancelled successfully!");

      rowMenuId.value = null;
      await fetchLeaves(); // Refresh the list
    } catch (err) {
      showToast(
        err.response?.data?.message ||
          err.message ||
          "Failed to cancel leave request",
        "error",
      );
    } finally {
      loading.value = false;
      loadingMessage.value = "";
    }
  }

  function validateForm() {
    Object.keys(errors).forEach((key) => (errors[key] = ""));
    let isValid = true;

    // Leave Type
    if (!form.leaveType) {
      errors.leaveType = "Please select a leave type.";
      isValid = false;
    }

    // From Date
    if (!form.fromDate) {
      errors.fromDate = "Please select the start date.";
      isValid = false;
    }

    // To Date
    if (!form.toDate) {
      errors.toDate = "Please select the end date.";
      isValid = false;
    } else if (
      form.fromDate &&
      new Date(form.toDate) < new Date(form.fromDate)
    ) {
      errors.toDate = "End date cannot be before start date.";
      isValid = false;
    }

    // Reason
    const reason = form.reason.trim();
    if (!reason) {
      errors.reason = "Please enter a reason for your leave.";
      isValid = false;
    } else if (reason.length < 6) {
      errors.reason = "Reason must be at least 6 characters.";
      isValid = false;
    } else if (reason.length > 500) {
      errors.reason = "Reason cannot exceed 500 characters.";
      isValid = false;
    }

    return isValid;
  }

  function resetForm() {
    form.leaveType = "";
    form.fromDate = "";
    form.fromDuration = "full day";
    form.toDate = "";
    form.toDuration = "full day";
    form.reason = "";

    // Clear errors
    Object.keys(errors).forEach((key) => (errors[key] = ""));
  }

  function clearFilters() {
    filters.search = "";
    filters.leave_type = "";
    filters.month = "";
    filters.year = new Date().getFullYear();
    pagination.value.page = 1;
    fetchLeaves();
  }

  function changePage(page) {
    pagination.value.page = page;
    fetchLeaves();
  }

  function openModal() {
    modalOpen.value = true;
    rowMenuId.value = null;
    submitError.value = "";
  }

  function closeModal() {
    modalOpen.value = false;
    resetForm();
    submitError.value = "";
  }

  function toggleRowMenu(id) {
    rowMenuId.value = rowMenuId.value === id ? null : id;
  }

  function badgeClass(status) {
    switch (status) {
      case "Approved":
        return "text-green-700 bg-green-50 ring-green-200";
      case "Declined":
      case "Rejected":
        return "text-red-700 bg-red-50 ring-red-200";
      case "Cancelled":
        return "text-slate-600 bg-slate-100 ring-slate-200";
      default:
        return "text-blue-700 bg-blue-50 ring-blue-200";
    }
  }

  function formatDateTime(dateString) {
    try {
      const date = new Date(dateString);
      const options = { year: "numeric", month: "short", day: "2-digit" };
      return date.toLocaleDateString(undefined, options);
    } catch {
      return "";
    }
  }

  // Watchers
  watch(
    () => filters.search,
    () => {
      pagination.value.page = 1;
    },
  );

  watch(
    () => filters.leave_type,
    () => {
      pagination.value.page = 1;
    },
  );

  watch(
    () => filters.month,
    () => {
      pagination.value.page = 1;
    },
  );

  watch(
    () => filters.year,
    () => {
      pagination.value.page = 1;
    },
  );

  const totalLeaveDays = computed(() => {
    if (!form.fromDate || !form.toDate) return "";

    const fromDate = new Date(form.fromDate);
    const toDate = new Date(form.toDate);

    let days = (toDate - fromDate) / (1000 * 60 * 60 * 24) + 1;
    if (days < 0) return "";

    let total = days;

    // Adjust based on fromDuration and toDuration
    if (
      form.fromDuration === "first half" ||
      form.fromDuration === "second half"
    ) {
      total -= 0.5;
    }

    if (form.toDuration === "first half" || form.toDuration === "second half") {
      total -= 0.5;
    }

    if (total <= 0) return "Half day";

    const integerPart = Math.floor(total);
    const fractionalPart = total - integerPart;

    if (fractionalPart >= 0.5) {
      return `${integerPart} /half day`;
    } else {
      return `${integerPart} day${integerPart > 1 ? "s" : ""}`;
    }
  });
  const selectedLeaveRemaining = computed(() => {
    const selected = leaveBalance.value?.balances?.find(
      (b) => b.type === form.leaveType,
    );
    return selected?.remaining ?? 0;
  });
  watch(
    () => ({ ...form }),
    () => {
      submitError.value = "";
    },
    { deep: true },
  );
  // Lifecycle
  onMounted(() => {
    fetchLeaves();
    store.dispatch("fetchMyLeaveBalance");
  });
</script>
<style scoped>
  table td {
    white-space: nowrap;

    text-overflow: ellipsis;
  }
</style>
